import {
	GetL1AssessmentsResponse,
	GetMigrationPlanTotalsResponse,
	GetInstancesResponse,
	GetFastshiftCustomerDomainsResponse,
	GetFastshiftCustomerDomainResponseData,
	GetCustomerDomainEngagementResponseData,
	GetFastshiftUserAccessResponse,
	GetFastshiftSupportRequestsResponseData,
	FastshiftTableControlsPayload,
	FastshiftOrganizationSummaryResponseData,
	GetFastshiftPortalUserConsentResponse,
	GetFastshiftTopSupportRequestsResponseData,
	FastshiftRunbook,
	FastshiftRunbookSummary,
	FastshiftDcInstanceDetails,
	GetFastshiftOrganizationDcInstancesResponseData,
	GetFastshiftOrganizationsResponseData,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { TABLE_IDS } from './constants';

type BaseThunk = {
	pending: boolean;
	error: string | null;
};

export type Org = {
	id: string;
	isAdmin: boolean;
	name: string;
};

export type Orgs = BaseThunk & {
	data: Org[];
};

export type MigrationPlanTotals = BaseThunk & {
	data: GetMigrationPlanTotalsResponse;
};

export type L1Assessments = BaseThunk & {
	data: GetL1AssessmentsResponse;
};

export type InstancesRequest = BaseThunk & {
	data: { instances: GetInstancesResponse['instances']; totalCount: number };
};

export type FastshiftCustomerDomainsState = BaseThunk & {
	data: GetFastshiftCustomerDomainsResponse | null;
};

export type CustomerDomainDetails = BaseThunk & {
	data: GetFastshiftCustomerDomainResponseData | null;
};

export type CustomerDomainOrg = NonNullable<
	CustomerDomainDetails['data']
>['orgsWithAdminRole'][number];

export type CustomerDomainEngagement = BaseThunk & {
	data: GetCustomerDomainEngagementResponseData | null;
};

export const FastshiftApiAbortError = 'AbortError';

export type UserAccess = BaseThunk & {
	data: GetFastshiftUserAccessResponse['data'] | null;
};

export type Organizations = BaseThunk & {
	domainId: string | null;
	data: GetFastshiftOrganizationsResponseData | null;
};

export type OrganizationSummary = BaseThunk & {
	data: FastshiftOrganizationSummaryResponseData | null;
};

export type OrganizationDcInstances = BaseThunk & {
	data: GetFastshiftOrganizationDcInstancesResponseData | null;
};

export type OrganizationDetail = {
	domainId: string | null;
	orgId: string | null;
	summary: OrganizationSummary;
	dcInstances: OrganizationDcInstances;
};

export type DcInstanceDetail = BaseThunk & {
	domainId: string | null;
	orgId: string | null;
	instanceId: string | null;
	data: FastshiftDcInstanceDetails | null;
};

export type SupportRequests = BaseThunk & {
	data: GetFastshiftSupportRequestsResponseData | null;
	lastFetchParams?: {
		customerDomainId: string;
		queryParams: {
			orgId?: string;
			permissionLevel?: string;
		};
	};
};

export type TopSupportRequests = BaseThunk & {
	data: GetFastshiftTopSupportRequestsResponseData | null;
	lastFetchParams?: {
		customerDomainId: string;
		queryParams: {
			orgId?: string;
			permissionLevel?: string;
		};
	};
};

export type RunbookSummaries = BaseThunk & {
	data: FastshiftRunbookSummary[] | null;
};

export type RunbookDetail = BaseThunk & {
	data: FastshiftRunbook | null;
};

export type RunbookStepAction = BaseThunk;

export type Runbooks = {
	summariesBySupportTicketId: Record<string, RunbookSummaries>;
	detailsByRunbookId: Record<string, RunbookDetail>;
	stepActionsByStepKey: Record<string, RunbookStepAction>;
};

export type TableId = (typeof TABLE_IDS)[keyof typeof TABLE_IDS];

// Extend BE payload with FE-specific filters
export type FastshiftPortalTableControls = FastshiftTableControlsPayload & {
	filters: Record<string, string | null>;
};

export type PageStateCache = Record<TableId, FastshiftPortalTableControls>;

export type PortalUserConsent = BaseThunk & {
	data: GetFastshiftPortalUserConsentResponse['data']['consent'] | null;
};
